import React, { useMemo } from 'react';
import { combineIntoOther } from '../chart';
import { cn } from '@/lib';
import styles from './StanSimpleBarChart.module.css';

export interface StanSimpleBarChartProps {
    /** Data to display as simple horizontal bars. */
    totals: Record<string, number>;
    /** Custom formatter for values in simple bars. */
    formatter?: (value: number) => string;
    /** Show only top N items, combining rest into "Other". 0 disables aggregation. */
    showTopN?: number;
    /** Label for the "Other" category when showTopN is used. */
    otherLabel?: string;
    /** Primary color for the bars. */
    color?: string;
    /** Extra class name applied to the chart wrapper. */
    className?: string;
    /** Loading state for the chart. Component will preserve previous data during loading. */
    loading?: boolean;
    /** Custom no data message. */
    noDataMessage?: string;
}

export const StanSimpleBarChart: React.FC<StanSimpleBarChartProps> = ({
    totals,
    formatter = v => String(v),
    showTopN = 0,
    otherLabel = 'Other',
    color,
    className,
    noDataMessage = 'No data available',
}) => {
    /**
     * Process and format data for simple horizontal bars.
     * Computes aggregated totals, finds max value, and calculates percentages.
     */
    const rows = useMemo(() => {
        if (!totals) return [];

        // Apply top-N aggregation if specified
        const processedTotals = showTopN > 0 ? combineIntoOther(totals, showTopN, otherLabel) : totals;

        // Find maximum value for percentage calculation
        let max = -Infinity;
        for (const v of Object.values(processedTotals)) {
            if (v > max) max = v;
        }

        // Transform data into renderable row format
        return Object.entries(processedTotals)
            .filter(([, v]) => v !== 0)
            .map(([lbl, value]) => {
                const percentageOfMax = max > 0 ? (value / max) * 100 : 0;
                return { label: lbl, value, formattedValue: formatter(value), percentageOfMax };
            })
            .sort((a, b) => b.value - a.value);
    }, [totals, showTopN, otherLabel, formatter]);

    // Always pad to 5 rows with placeholders if needed
    const displayRows = useMemo(() => {
        const paddedRows = [...rows];
        const rowsNeeded = 6 - rows.length;

        // Add placeholder rows to reach 5 total
        if (rowsNeeded > 0) {
            for (let i = 0; i < rowsNeeded; i++) {
                paddedRows.push({
                    label: '\u00A0', // Non-breaking space for placeholder
                    value: 0,
                    formattedValue: '',
                    percentageOfMax: 0,
                });
            }
        }

        return paddedRows;
    }, [rows]);

    const hasNoData = !totals || Object.keys(totals).length === 0 || rows.length === 0;

    return (
        <div className={cn('relative', className)}>
            <div className={styles.simpleContainer}>
                <div className={styles.simpleInner}>
                    {displayRows.map((row, index) => (
                        // Index-based keys are intentional here for smooth animations
                        // eslint-disable-next-line
                        <div key={`bar-row-${index}`} className={styles.barRow}>
                            <div className={styles.barLabel}>
                                <div className={styles.barLabelText}>{row.label}</div>
                            </div>
                            <div className={styles.barContainer}>
                                <span
                                    className={styles.barFill}
                                    style={
                                        {
                                            '--bar-width': `${row.percentageOfMax}%`,
                                            backgroundColor: color || 'var(--color-brand-main)',
                                        } as React.CSSProperties
                                    }
                                >
                                    <span className={styles.barValue}>{row.formattedValue}</span>
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {hasNoData && (
                <div className='absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm'>
                    <div className='caption-2-regular'>{noDataMessage}</div>
                </div>
            )}
        </div>
    );
};

export default StanSimpleBarChart;
